import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, hasRole } from '../contexts/AuthContext';
import api from '../api/client';
import {
  LayoutDashboard, Truck, Users, FileText, ClipboardCheck, Send, Wrench,
  BarChart3, FileSignature, Receipt, Bell, LogOut, Settings, Menu, X,
  ChevronDown, Layers, Boxes, BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ROLE_LABELS } from '../lib/format';
import { ScrollArea } from '../components/ui/scroll-area';
import { formatDateTime } from '../lib/format';
import ForcePasswordResetModal from './ForcePasswordResetModal';
import RecentSignInsDialog from './RecentSignInsDialog';
import { History } from 'lucide-react';

const MENU = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin','admin','supervisor','approver','requestor','operator'] },
  { to: '/equipment', label: 'Equipment', icon: Truck, roles: ['super_admin','admin','supervisor','approver','operator','requestor'] },
  { to: '/operators', label: 'Operators', icon: Users, roles: ['super_admin','admin','supervisor','approver'] },
  { to: '/equipment-requests', label: 'Equipment Requests', icon: FileText, roles: ['super_admin','admin','supervisor','approver','requestor'] },
  { to: '/inspections', label: 'Inspections', icon: ClipboardCheck, roles: ['super_admin','admin','supervisor','approver'] },
  { to: '/equipment-queues', label: 'Equipment Queues', icon: Layers, roles: ['super_admin','admin','supervisor','approver'] },
  { to: '/mobilizations', label: 'Mobilizations', icon: Send, roles: ['super_admin','admin','supervisor','approver','operator'] },
  { to: '/maintenance', label: 'Maintenance', icon: Wrench, roles: ['super_admin','admin','supervisor'] },
  { to: '/consumables', label: 'Consumables', icon: Boxes, roles: ['super_admin','admin','supervisor','approver','operator','requestor'] },
  { to: '/utilizations', label: 'Utilizations', icon: BarChart3, roles: ['super_admin','admin','supervisor'] },
  { to: '/contracts', label: 'Contracts', icon: FileSignature, roles: ['super_admin','admin'] },
  { to: '/billings', label: 'Billing', icon: Receipt, roles: ['super_admin','admin'] },
  { to: '/settings', label: 'Settings', icon: Settings, roles: ['super_admin','admin'] },
  { to: '/guide', label: 'User Guide', icon: BookOpen, roles: ['super_admin','admin','supervisor','approver','requestor','operator'] },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [signInsOpen, setSignInsOpen] = useState(false);
  const unread = notifs.filter((n) => !n.is_read).length;

  const loadNotifs = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      setNotifs(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    loadNotifs();
  };

  const visibleMenu = MENU.filter((m) => !user || m.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[hsl(210,40%,98%)] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 inset-y-0 left-0 z-40 w-64 brand-gradient flex flex-col transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 h-screen`}>
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <div className="flex items-center gap-3 text-white">
            <div className="h-9 w-9 rounded-lg bg-amber-400 grid place-items-center text-[hsl(217,91%,18%)]">
              <Truck className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div>
              <div className="font-bold text-base leading-none">ESD</div>
              <div className="text-[10px] text-blue-200/80 leading-none mt-1">Equipment Solutions Dept.</div>
            </div>
          </div>
          <button className="ml-auto lg:hidden text-white" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <ScrollArea className="flex-1 py-3 px-3">
          <nav className="space-y-1">
            {visibleMenu.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={() => setOpen(false)}>
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </ScrollArea>
        <div className="p-3 border-t border-white/10 text-[11px] text-blue-200/70">
          v1.0 • PIVI ESD
        </div>
      </aside>

      {/* Backdrop */}
      {open && <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-6 sticky top-0 z-20">
          <Button variant="ghost" size="icon" className="lg:hidden mr-2" onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden md:block">
            <div className="text-sm text-slate-500">Welcome back,</div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">{user?.full_name}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Notifications */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute top-1.5 right-1.5 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-[10px] text-white font-bold grid place-items-center">{unread}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-0" align="end">
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="font-semibold text-sm">Notifications</div>
                  {unread > 0 && (
                    <button onClick={markAllRead} className="text-xs text-[hsl(217,91%,24%)] hover:underline">Mark all read</button>
                  )}
                </div>
                <ScrollArea className="max-h-96">
                  {notifs.length === 0 ? (
                    <div className="p-6 text-sm text-slate-500 text-center">No notifications yet.</div>
                  ) : (
                    <ul className="divide-y">
                      {notifs.map((n) => (
                        <li key={n.id} className={`p-3 hover:bg-slate-50 cursor-pointer ${!n.is_read ? 'bg-blue-50/40' : ''}`} onClick={() => { if (n.link) navigate(n.link); }}>
                          <div className="flex items-start gap-2">
                            <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.is_read ? 'bg-slate-300' : 'bg-[hsl(217,91%,40%)]'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900">{n.title}</div>
                              <div className="text-xs text-slate-600 truncate">{n.message}</div>
                              <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(n.created_at)}</div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100">
                  <div className="h-8 w-8 rounded-full bg-[hsl(217,91%,24%)] text-white grid place-items-center text-sm font-bold">
                    {(user?.full_name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('')}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium leading-none">{user?.full_name}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{ROLE_LABELS[user?.role]}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.full_name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  <Badge variant="secondary">{ROLE_LABELS[user?.role]}</Badge>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSignInsOpen(true); }} data-testid="user-menu-recent-signins">
                  <History className="h-4 w-4 mr-2" /> Recent sign-ins
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-700" data-testid="user-menu-signout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      <ForcePasswordResetModal />
      <RecentSignInsDialog open={signInsOpen} onOpenChange={setSignInsOpen} />
    </div>
  );
};

export default Layout;
